import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Waves, Trees, Car, Wifi, UtensilsCrossed, Sparkles, Star, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { hotelInfo, facilities, testimonials, rooms as mockRooms } from '../mockData';
import { roomTypesApi } from '../services/api';
import { useSEO } from '../hooks/useSEO';
import landingVideo from '../assets/images/hero-video.webm';
import landingImage from '../assets/images/landing.webp';
import hotelInteriorImage from '../assets/images/entrance.webp';

const HomePage = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [roomTypes, setRoomTypes] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const videoRef = useRef(null);

  const jsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Hotel',
    name: hotelInfo.name,
    description: hotelInfo.description,
    url: 'https://olivoakvilla.com',
    telephone: hotelInfo.contact.phone,
    email: hotelInfo.contact.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '14180 Gunhill Avenue',
      addressLocality: 'Harare',
      addressCountry: 'ZW'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: hotelInfo.location.coordinates.lat,
      longitude: hotelInfo.location.coordinates.lng
    }
  }), []);

  useSEO({
    title: 'OlivOak Villa | Refined Comfort. Tranquil Living.',
    description: 'OlivOak Villa — luxury accommodation in Harare, Zimbabwe. Enjoy refined comfort, tranquil living, and exceptional hospitality. Book your stay today.',
    jsonLd
  });

  const iconMap = {
    Waves,
    Trees,
    Car,
    Wifi,
    UtensilsCrossed,
    Sparkles
  };

  // Fetch room types from API
  useEffect(() => {
    const fetchRoomTypes = async () => {
      try {
        setRoomsLoading(true);
        const data = await roomTypesApi.getAll(true); // active only
        // Only show room types that have an image
        const withImages = data.filter(rt => rt.image_url);
        setRoomTypes(withImages.length > 0 ? withImages : []);
      } catch (err) {
        console.error('Failed to fetch room types:', err);
        setRoomTypes([]);
      } finally {
        setRoomsLoading(false);
      }
    };
    fetchRoomTypes();
  }, []);

  // Hero video URL - replace with your actual video URL
  const heroVideoUrl = landingVideo;
  // Fallback image while video loads
  const heroImageUrl = landingImage;

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handleCanPlay = () => {
        setVideoLoaded(true);
        video.play().catch(() => {
          // Autoplay might be blocked, that's okay
          setVideoError(true);
        });
      };
      
      const handleError = () => {
        setVideoError(true);
      };

      video.addEventListener('canplaythrough', handleCanPlay);
      video.addEventListener('error', handleError);

      return () => {
        video.removeEventListener('canplaythrough', handleCanPlay);
        video.removeEventListener('error', handleError);
      };
    }
  }, []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Hero Section with Video Background */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Fallback Image Background - shown while video loads or if video fails */}
        <div
          className="absolute inset-0 z-0 transition-opacity duration-1000"
          style={{
            backgroundImage: `url('${heroImageUrl}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            opacity: videoLoaded && !videoError ? 0 : 1
          }}
        />
        
        {/* Video Background */}
        {!videoError && (
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover z-0 transition-opacity duration-1000"
            style={{ opacity: videoLoaded ? 1 : 0 }}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          >
            <source src={heroVideoUrl} type="video/webm" />
          </video>
        )}
        
        {/* Dark Overlay */}
        <div 
          className="absolute inset-0 z-[1]"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
        />
        
        {/* Hero Content */}
        <div className="text-center z-10 px-6">
          <h1
            className="hero-large mb-6"
            style={{ color: '#ffffff', maxWidth: '800px', margin: '0 auto' }}
          >
            {hotelInfo.tagline}
          </h1>
          <p
            className="body-large mb-10"
            style={{ color: '#f0f0f0', maxWidth: '600px', margin: '0 auto 40px' }}
          >
            {hotelInfo.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/booking">
              <Button className="btn-primary">
                Check Availability
              </Button>
            </Link>
            <Link to="/rooms">
              <button
                className="btn-secondary"
                style={{
                  color: '#ffffff',
                  padding: '19px 32px',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'transparent'
                }}
              >
                Explore Rooms
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="section-padding" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div
                className="inline-block px-4 py-2 mb-6"
                style={{
                  backgroundColor: 'var(--bg-olive-light)',
                  border: '1px solid var(--olive-primary)'
                }}
              >
                <span
                  className="body-small"
                  style={{
                    color: 'var(--olive-dark)',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}
                >
                  About Us
                </span>
              </div>
              <h2 className="heading-1 mb-6">Welcome to OlivOak Villa</h2>
              <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
                Nestled in the heart of Zimbabwe, OlivOak Villa offers an unparalleled experience of
                refined luxury and tranquil comfort. Every detail has been thoughtfully curated to
                provide our guests with an exceptional stay.
              </p>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                From our elegantly appointed rooms to our world-class facilities, we invite you to
                experience hospitality that goes beyond expectations.
              </p>
            </div>
            <div className="hover-lift" style={{ position: 'relative' }}>
              <div
                style={{
                  position: 'absolute',
                  top: '-20px',
                  left: '-20px',
                  width: '100px',
                  height: '100px',
                  backgroundColor: 'var(--olive-light)',
                  opacity: 0.3,
                  zIndex: 0
                }}
              />
              <img
                src={hotelInteriorImage}
                alt="Hotel Interior"
                className="w-full h-[500px] object-cover relative z-10"
                style={{ border: '1px solid var(--border-light)' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Rooms Preview */}
      <section className="section-padding" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-1 mb-4">Rooms & Suites</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Discover your perfect retreat from our selection of elegantly designed accommodations
            </p>
          </div>

          {roomsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin" size={32} style={{ color: 'var(--olive-primary)' }} />
            </div>
          ) : roomTypes.length > 0 ? (
            /* Server-side room types */
            <div className={`grid grid-cols-1 md:grid-cols-2 ${roomTypes.length >= 3 ? 'lg:grid-cols-3' : ''} ${roomTypes.length >= 4 ? 'xl:grid-cols-4' : ''} gap-8`}>
              {roomTypes.map((rt) => (
                <Link
                  key={rt.id}
                  to={`/rooms?type=${rt.id}`}
                  style={{ textDecoration: 'none' }}
                  className="hover-lift h-full"
                >
                  <div
                    className="flex flex-col h-full"
                    style={{
                      backgroundColor: 'var(--bg-primary)',
                      overflow: 'hidden'
                    }}
                  >
                    <img
                      src={rt.image_url}
                      alt={rt.name}
                      className="w-full h-64 object-cover"
                    />
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="heading-3 mb-2">{rt.name}</h3>
                      <p className="body-small mb-4 line-clamp-3" style={{ color: 'var(--text-secondary)' }}>
                        {rt.description || `Up to ${rt.max_occupancy} guests`}
                      </p>
                      <div className="flex items-center justify-between mt-auto">
                        <span className="body-small" style={{ color: 'var(--text-primary)' }}>
                          From ${Number(rt.base_rate_single).toFixed(0)}/night
                        </span>
                        <ArrowRight size={16} style={{ color: 'var(--text-primary)' }} />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            /* Fallback to mock data */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {mockRooms.map((room) => (
                <Link
                  key={room.id}
                  to={`/rooms/${room.id}`}
                  style={{ textDecoration: 'none' }}
                  className="hover-lift h-full"
                >
                  <div
                    className="flex flex-col h-full"
                    style={{
                      backgroundColor: 'var(--bg-primary)',
                      overflow: 'hidden'
                    }}
                  >
                    <img
                      src={room.images[0]}
                      alt={room.name}
                      className="w-full h-64 object-cover"
                    />
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="heading-3 mb-2">{room.name}</h3>
                      <p className="body-small mb-4 line-clamp-3" style={{ color: 'var(--text-secondary)' }}>
                        {room.shortDescription}
                      </p>
                      <div className="flex items-center justify-between mt-auto">
                        <span className="body-small" style={{ color: 'var(--text-primary)' }}>
                          From ${room.price.USD}/night
                        </span>
                        <ArrowRight size={16} style={{ color: 'var(--text-primary)' }} />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/rooms">
              <Button className="btn-primary">View All Rooms</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Facilities Highlights */}
      <section className="section-padding" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-1 mb-4">Our Facilities</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Everything you need for a perfect stay
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {facilities.map((facility) => {
              const IconComponent = iconMap[facility.icon];
              return (
                <div
                  key={facility.name}
                  className="text-center p-8 hover-lift"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid var(--border-light)'
                  }}
                >
                  <div
                    className="inline-flex items-center justify-center w-16 h-16 mb-6"
                    style={{
                      border: '2px solid var(--olive-primary)',
                      color: 'var(--olive-primary)'
                    }}
                  >
                    {IconComponent && <IconComponent size={32} />}
                  </div>
                  <h3 className="heading-3 mb-3">{facility.name}</h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {facility.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-1 mb-4">Guest Experiences</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Hear what our guests have to say about their stay
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div
              className="p-12 text-center"
              style={{
                backgroundColor: 'var(--bg-primary)',
                border: '1px solid var(--border-light)'
              }}
            >
              <div className="flex justify-center gap-1 mb-6">
                {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                  <Star key={i} size={20} fill="var(--text-primary)" style={{ color: 'var(--text-primary)' }} />
                ))}
              </div>
              <p
                className="body-large mb-8 italic"
                style={{ color: 'var(--text-primary)', lineHeight: '1.8' }}
              >
                "{testimonials[currentTestimonial].comment}"
              </p>
              <p className="heading-3 mb-2">{testimonials[currentTestimonial].name}</p>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                {testimonials[currentTestimonial].country}
              </p>
            </div>

            <div className="flex justify-center gap-4 mt-8">
              <button
                onClick={prevTestimonial}
                className="p-3 border hover:bg-[var(--bg-subtle)] transition-all"
                style={{ borderColor: 'var(--border-medium)', color: 'var(--text-primary)' }}
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={nextTestimonial}
                className="p-3 border hover:bg-[var(--bg-subtle)] transition-all"
                style={{ borderColor: 'var(--border-medium)', color: 'var(--text-primary)' }}
              >
                <ChevronRight size={24} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="section-padding" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="heading-1 mb-6">Find Us</h2>
              <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
                Conveniently located in Harare, Zimbabwe. Easy access to major attractions and
                business districts.
              </p>
              <p className="body-regular mb-8" style={{ color: 'var(--text-secondary)' }}>
                {hotelInfo.location.address}
              </p>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${hotelInfo.location.coordinates.lat},${hotelInfo.location.coordinates.lng}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="btn-primary">Get Directions</Button>
              </a>
            </div>
            <div className="hover-lift">
              <iframe
                src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1899.0!2d${hotelInfo.location.coordinates.lng}!3d${hotelInfo.location.coordinates.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1931a54ebd80ead9%3A0x46218f8742fa63a0!2s14180%20Gun%20Hill%20Ave%2C%20Harare!5e0!3m2!1sen!2szw!4v1234567890123!5m2!1sen!2szw`}
                width="100%"
                height="450"
                style={{ border: '1px solid var(--border-light)' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Hotel Location"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
